<template>
  <backoffice-container>
    <b-card
      no-body
      :class="isMobile ? 'p-2' : 'p-3'"
      :style="
        isMobile
          ? 'min-height: calc(100vh - 100px)'
          : 'min-height: calc(100vh - 125px)'
      "
    >
      <div class="d-flex flex-wrap justify-content-between ml-50 mb-2">
        <h4>{{ $t("backoffice.transactions.title") }}</h4>
        <b-button v-b-modal.new-transaction variant="primary" class="ml-1">
          {{ $t("backoffice.transactions.new-transaction") }}
        </b-button>
      </div>
      <div v-if="isLoading" class="h-100 d-flex align-items-center">
        <b-spinner class="d-block mx-auto mt-3" variant="primary" />
      </div>
      <div v-else>
        <div v-if="items && items.length > 0">
          <div
            v-for="(item, index) in Object.values(filteredItems)"
            v-show="items && items.length > 0"
            :key="index"
            class="w-100"
          >
            <div
              v-if="showSpace"
              :class="isMobile ? '' : 'align-items-center'"
              class="d-flex justify-content-between"
            >
              <b-link
                class="text-dark d-flex w-100"
                :class="isMobile ? '' : 'align-items-center'"
                :href="getCollectiveUrl(item.emitter)"
                target="_blank"
              >
                <collective-logo :logo="item.emitter.logoURL" :size="30" />
                <div class="ml-50">
                  <p class="mb-0 font-weight-bold">
                    {{ translate(item.emitter.name) }}
                  </p>
                  <p
                    v-if="item.emitter.headline"
                    class="small mb-0 mt-n25 text-muted"
                  >
                    {{ translate(item.emitter.headline) }}
                  </p>
                </div>
              </b-link>
              <div
                class="d-flex"
                :class="
                  isMobile
                    ? 'flex-column justify-content-center'
                    : ' aling-items-center'
                "
              >
                <b-badge
                  variant="light-primary"
                  class="font-weight-normal pointer"
                  :class="isMobile ? 'transaction-1 mt-50 mb-50' : 'transaction-0 mr-50'"
                  @click="downloadTransactions(item.emitter)"
                >
                  <span v-if="isLoadingButton">
                    <b-spinner small class="spinner" /> {{ $t("loading") }}
                  </span>
                  <span v-else>
                    <feather-icon icon="DownloadIcon" class="mr-25" />
                    {{ $t("backoffice.members.export") }}
                  </span>
                </b-badge>
                <b-badge
                  pill
                  :class="isMobile ? 'transaction-0' : 'transaction-1'"
                  :variant="getColorSpace(item.emitter.mainType)"
                  class="text-capitalize check-button"
                >
                  {{ getStatusSpace(item.emitter.mainType) }}
                </b-badge>
              </div>
            </div>
            <app-scroll>
              <!-- Transactions table for mobile version -->
              <transactions-table-mobile
                v-if="isMobile"
                :busy="isLoading || isLoadingNextPage"
                :item="item"
                @isSpaceNameShown="(response) => (showSpace = response)"
                @edit-transaction="editTransaction"
                @delete-transaction="deleteTransaction"
              />
              <!-- Transactions table for desktop version -->
              <transactions-table-web
                v-else
                :busy="isLoading || isLoadingNextPage"
                :item="item"
                @edit-transaction="editTransaction"
                @delete-transaction="deleteTransaction"
              />
            </app-scroll>
          </div>
        </div>
        <div
          v-else
          class="d-flex placeholder align-item-center justify-content-center flex-column my-2"
          style="min-height: 45vh"
        >
          <b-img :src="paymentsPlaceholder" class="placeholder-img" center />
          <div class="mt-2 text-center">
            <p class="text-primary font-weight-500">
              {{
                $t("available.message", {
                  itemName: $t("backoffice.transactions.title"),
                })
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Loading... -->
      <loading-modal
        v-model="isLoadingButton"
        v-ripple.400="'rgba(234, 84, 85, 0.15)'"
      />

      <!-- New transaction modal -->
      <b-modal
        id="new-transaction"
        class="transactions-modal"
        :title="$t('backoffice.transactions.new-transaction')"
        :ok-title="$t('form.actions.save')"
        ok-only
        centered
        size="lg"
        :ok-disabled="isDisabled"
        no-close-on-backdrop
        @hidden="resetForm()"
        @shown="atFormShown()"
        @ok="saveTransaction()"
      >
        <template #modal-header>
          <language-selector-header
            :title="
              isEditing
                ? $t('backoffice.transactions.edit-transaction')
                : $t('backoffice.transactions.create-transaction')
            "
            @closeModal="closeModal"
            @selectLanguage="(language) => (selectedLanguage = language)"
          />
        </template>
        <!-- Reference -->
        <b-form-group class="flex-grow-1 mb-2 mt-50">
          <b-row>
            <!-- Reference -->
            <b-col class="col-xs-12">
              <label for="transaction-ref" class="font-weight-bold">
                {{ $t("backoffice.transactions.fields.ref") }}
              </label>
              <b-form-input
                v-model="currentTransaction.ref"
                label-for="transaction-ref"
              />
            </b-col>
          </b-row>
        </b-form-group>
        <!-- Dates -->
        <b-form-group class="flex-grow-1 mb-2 mt-50">
          <b-row>
            <!-- Issuing Date -->
            <b-col class="col-xs-6">
              <label for="transaction-date" class="font-weight-bold">
                {{ $t("backoffice.transactions.fields.issuing-date") }}
              </label>
              <VueCtkDateTimePicker
                id="date"
                v-model="currentTransaction.date"
                name="date"
                :locale="locale"
                format="YYYY-MM-DD"
                formatted="ll"
                :label="$t('backoffice.transactions.fields.issuing-date')"
                label-for="transaction-date"
                color="var(--primary)"
                no-button-now
                only-date
                button-color="var(--primary)"
              />
            </b-col>
            <!-- Due Date -->
            <b-col class="col-xs-6">
              <label for="transaction-date" class="font-weight-bold">
                {{ $t("backoffice.transactions.fields.due-date") }}
              </label>
              <VueCtkDateTimePicker
                id="date"
                v-model="currentTransaction.dueDate"
                name="date"
                :locale="locale"
                format="YYYY-MM-DD"
                formatted="ll"
                :label="$t('backoffice.transactions.fields.due-date')"
                label-for="transaction-date"
                color="var(--primary)"
                no-button-now
                only-date
                button-color="var(--primary)"
              />
            </b-col>
          </b-row>
        </b-form-group>
        <!-- Amount and currency -->
        <b-form-group class="flex-grow-1 mb-2">
          <b-row>
            <!-- Amount -->
            <b-col class="col-xs-6">
              <label for="transaction-price" class="font-weight-bold">
                Amount *
              </label>
              <b-form-input
                v-model="currentTransaction.price"
                type="number"
                label-for="transaction-price"
                required
              />
            </b-col>
            <!-- Currency -->
            <b-col class="col-xs-6">
              <label for="transaction-currency" class="font-weight-bold">
                Currency
              </label>
              <b-form-select
                id="transaction-currency"
                v-model="currentTransaction.currencyId"
                :options="currencyCode"
                placeholder="Select a Currency..."
                label-type="text"
                @keydown.enter.prevent="onEnter"
              />
            </b-col>
          </b-row>
        </b-form-group>
        <!-- Apps -->
        <!--b-form-group>
          <label for="rol-name" class="font-weight-bold">
             {{ $t('common.apps') }}
          </label>
          <v-select
            v-model="currentTransaction.modelType"
            class="select-control p-0"
            :placeholder="$t('form.select-multiple-placeholder')"
            clearable
            :multiple="false"
            :label="locale"
            :searchable="false"
            :get-option-label="getOptionsLabel"
            :options="modelOptions"
          />
        </b-form-group-->
        <!-- Organization Sender / Emitter -->
        <b-form-group>
          <label for="rol-name" class="font-weight-bold">
             {{ $t('backoffice.transactions.fields.sender') }}
          </label>
          <v-select
            v-if="currentCollective.mainType !== 'Organization'"
            v-model="currentTransaction.transactionEmitterKey"
            class="select-control p-0"
            :placeholder="$t('form.select-multiple-placeholder')"
            clearable
            :multiple="false"
            :label="locale"
            :searchable="false"
            :get-option-label="(label) => translate(label.name)"
            :options="senderOrganizationsOptions"
          />
          <div v-else>
            {{ translate(currentCollective.name) }}
          </div>
        </b-form-group>
        <!-- Organization Receptor / Collector -->
        <b-form-group>
          <label for="rol-name" class="font-weight-bold">
             {{ $t('backoffice.transactions.fields.receptor') }}
          </label>
          <v-select
            v-model="transactionCollector"
            class="select-control p-0"
            :placeholder="$t('form.select-multiple-placeholder')"
            clearable
            :multiple="false"
            :label="locale"
            :searchable="false"
            :get-option-label="(label) => translate(label.name)"
            :options="receptorOrganizationsOptions"
          />
        </b-form-group>
        <!-- Transaction date -->
        <!--b-form-group>
          <label for="rol-name" class="font-weight-bold">
             {{ $t('backoffice.transactions.fields.issuing-date') }}
          </label>
          
        </b-form-group-->
        <!-- Transaction file -->
        <!--b-form-group>
          <label for="rol-name" class="font-weight-bold">
             {{ $t('backoffice.transactions.fields.file') }}
          </label>
          
        </b-form-group-->
        <!-- Attach file -->
        <b-form-group>
          <label for="rol-name" class="font-weight-bold">
             {{ $t('backoffice.transactions.fields.file') }}
          </label>
          <file-upload
            v-model="attachments"
            class="mw-100"
            cancelable
            type="imageOrPdf"
            :multiple="false"
            :placeholder="$t('backoffice.transactions.fields.attach')"
            :drop-placeholder="$t('backoffice.transactions.fields.drop-placeholder')"
          />
        </b-form-group>
      </b-modal>
    </b-card>
  </backoffice-container>
</template>

<script>
import Ripple from 'vue-ripple-directive';
import BackofficeContainer from "@/backoffice/components/BackofficeContainer.vue";
import ToastNotificationsMixin from "@core/mixins/toast-notifications/ToastNotificationsMixin";
import TransactionsPlaceholder from "@/assets/images/placeholders/light/transactions.svg";
import AppScroll from "@/@core/components/app-scroll/AppScroll.vue";
import { translateTranslationTable } from "@/@core/libs/i18n/utils";
import CollectiveLogo from "@core/components/collective-logo/CollectiveLogo.vue";
import TransactionsTableWeb from "@/backoffice/components/transactions/TransactionsTableWeb.vue";
import TransactionsTableMobile from "@/backoffice/components/transactions/TransactionsTableMobile.vue";
import { getCollectiveUrl } from "@core/utils/collective";
import LoadingModal from "@core/components/modal/LoadingModal.vue";
import LanguageSelectorHeader from "@core/components/modal/LanguageSelectorHeader.vue";
import vSelect from 'vue-select';
import Service from '@/config/service-identifiers';
import { MainType } from '@copernicsw/community-common';
import FileUpload from '@core/components/files/FileUpload.vue';

export default {
  name: "BackofficeTransactions",
  components: {
    BackofficeContainer,
    AppScroll,
    CollectiveLogo,
    TransactionsTableWeb,
    TransactionsTableMobile,
    LoadingModal,
    LanguageSelectorHeader,
    FileUpload,
    vSelect
  },
  directives: {
    Ripple,
  },
  mixins: [ToastNotificationsMixin],
  data() {
    return {
      isLoading: false,
      isLoadingNextPage: false,
      isLoadingButton: false,
      showSpace: true,
      currencyCode: [{ value: 45, text: 'Select a Currency...' }],
      transactionCollector: null,
      attachments: null,
      editingTransactionKey: null,
      currentTransaction: {
        ref: null,
        // modelType: "organization",
        // modelKey: null,
        // fromOrgKey: null,
        transactionEmitterType: "community",
        transactionEmitterKey: null,
        transactionCollectorType: "community",
        transactionCollectorKey: null,
        priceId: null,
        subscriptionPlanId: null,
        mainCommunitySlug: null,
        currencyId: 45,       //euro
        currencyPriceItemsWithoutFee: 0,
        currencyPriceItemsFee: 0,
        currencyAppFee: 0,
        currencyPriceTotal: 0,
        status: "succeeded",  //succeeded, pending, failed, refunded, canceled, requires_action, processing, requires_payment_method
        createdByUserId: null,
        //Billing
        hasBilling: 0,
        billingType: null,  //company/particular
        billingName: null,
        billingSurname: null,
        billingEmail: null,
        billingPhoneNumber: null,
        billingNifNieCif: null,
        billingCountryId: null,
        billingProvince: null,
        billingPostalCode: null,
        billingLocation: null,
        billingAddress1: null,
        billingAddress2: null,
      },
      statusOptions: {
        'succeeded': 'Succeeded',
        'pending': 'Pending',
        'processing': 'Processing',
        'failed': 'Failed',
        'refunded': 'Refunded',
        'canceled': 'Cancelled',
        'requires_action': 'Requires action',
        'requires_payment_method': 'Requires transaction method'
      },
      billingTypeOptions: {
        'company': 'Company',
        'particular': 'Particular'
      },
      isValid: null,
      selectedLanguage: this.currentLocale,
      isEditing: false,
    };
  },
  computed: {
    currentCollective() {
      return this.$store.getters.currentCollective?.slug ? this.$store.getters.currentCollective : null;
    },
    mainCollective() {
      return this.$store.getters.mainCollective?.slug ? this.$store.getters.mainCollective : null;
    },
    mainSpace() {
      return this.mainCollective ?? this.currentCollective;
    },
    paymentsPlaceholder() {
      return TransactionsPlaceholder;
    },
    itemsData() {
      return this.$store.getters.transactions?.[this.currentCollective.key];
    },
    items() {
      return this.itemsData?.unpaginated;
    },
    isMobile() {
      return window.innerWidth < 700;
    },
    locale() {
      return this.$store.state.locale.currentLocale;
    },
    senderOrganizationsOptions() {
      const allOrgs = this.$store.getters.communitiesOthers?.organizations?.unpaginated;
      if(allOrgs && this.currentCollective?.mainType === 'Organization'){
        // Select the current org key
        this.currentTransaction.transactionEmitterKey = this.currentCollective.key;
        // Map all orgs to send only the current
      // console.log('senderOrganizationsOptions', [this.currentCollective]);
        return [this.currentCollective];
      }
      return allOrgs;
    },
    receptorOrganizationsOptions() {
      const allOrgs = this.$store.getters.communitiesOthers?.organizations?.unpaginated;
      if(allOrgs && this.currentCollective?.mainType === 'Organization'){
        const allOrgsWithoutCurrent = [];
        allOrgs.forEach(org => {
          if(org.key !== this.currentCollective.key){
            allOrgsWithoutCurrent.push(org);
          }
        });
        return allOrgsWithoutCurrent;
      }
      // console.log('receptorOrganizationsOptions', allOrgs);
      return allOrgs;
    },
    filteredItems() {
      const filteredItems = {}; // divididos por emisor
      // console.log('transactions raw list:', this.items);
      this.items?.forEach((item) => {
        if (item.emitter?.key) {
          // console.log('item.emitter.key', item.emitter.key);

          const emitterKeyIncluded = Object.keys(filteredItems).includes(item.emitter.key);
          // console.log('emitterKeyIncluded', emitterKeyIncluded);
          // si no hay un apartado para este emisor, lo creamos
          if (!emitterKeyIncluded) {
            filteredItems[item.emitter.key] = {
              emitter: item.emitter,
              transactions: [],
            };
          }  
          
          // añadimos esta transacción al apartado del emisor
          filteredItems[item.emitter.key].transactions.push(item);
        } else {
          console.log("This item does not have a linked community:", item);
        }
      });
      return filteredItems;
    },
    isDisabled() {
      return false;
    },
    modelOptions() {
      const modelTypesObj = this.$store.getters.modelTypes;
      const notApply = [
        'chat-individual',
        'chat-group',
        'chat',
        'map',
        'calendar',
        'meeting',
        'auditorium',
        'workspace',
        'community',
        'analytic',
        'transaction',
        'classifier',
        'custom-field',
        'live-streaming'
      ];
      let newObj = [];
      //use only correct apps
      modelTypesObj.forEach((app) => {
        if(!notApply.includes(app.addonSingular)){
          newObj.push(app);
        }
      });
      return newObj;
    },
    currentLocale() {
      return this.$store.state.locale.currentLocale;
    },
  },
  async created() {
    //console.log('this.mainSpace', this.mainSpace);
    await this.fetchData();
    await this.fetchOrganizations();  //TODO: better on open modal
    //console.log('this.organizationsOptions', this.organizationsOptions);
    await this.fetchCurrencies();
    // If it is an org:
    // console.log('this.currentCollective?.mainType', this.currentCollective?.mainType);
    if(this.currentCollective?.mainType === 'Organization'){
      // console.log('Setting fromOrgKey to current space key')
      this.currentTransaction.transactionEmitterKey = this.currentCollective.key;
    }
  },
  methods: {
    // Get transactions
    async fetchData(force = false) {
      this.isLoading = true;
      const requestConfig = {};
      if (this.currentCollective.parentKey) {
        requestConfig.forCommunity = true;
      } else {
        requestConfig.forMain = true;
      }
      await this.$store.dispatch("getItems", {
        itemType: "transactions",
        // customName: "transactions",
        storedKey: this.currentCollective.key,  //"transactions",
        page: 1,
        perPage: 20,
        forceAPICall: force,
        requestConfig,
      });
      this.isLoading = false;
    },
    translate(field) {
      return translateTranslationTable(this.currentLocale,field);
      //return translateTranslationTable(this.selectedLanguage, field);
    },
    async downloadTransactions(community) {
      this.isLoadingButton = true;
      const response = await this.$store.dispatch("exportFiles", {
        itemType: "/backoffice/transactions/export",
        requestConfig: {
          communitySlug: community.slug,
        },
      });
      // console.log('response', response);
      if(response.data.file){
        // the file arrives in the response
        setTimeout(this.csv(response, community), 10000);
      }else{
        if(response.data.status === 'pending'){
          // the file is sent to email
          this.notifySuccess(response.data.message);
        }else{
          this.notifyError(response.data.message);
        }
      }
      this.isLoadingButton = false;
    },
    csv(response, community) {
      const hiddenElement = document.createElement("a");
      hiddenElement.href = response.data.file;
      hiddenElement.target = "_blank";
      hiddenElement.download = `${this.translate(community.name)}Transactions.csv`;
      hiddenElement.click();
    },
    getMainType(mainTypeId) {
      // console.log('mainTypeId', mainTypeId);
      // console.log('getMainType MainType[mainTypeId]', MainType[mainTypeId]);
      return MainType[mainTypeId];
    },
    getStatusSpace(mainType) {
      // console.log('mainType', mainType);
      if(!mainType){
        return 'Space';
      }
      const type = {
        Community: this.$t("collectives.items.Community"),
        Subcommunity: this.$t("collectives.items.Subcommunity"),
        Event: this.$t("collectives.items.Event"),
        Section: this.$t("collectives.items.Section"),
        Course: this.$t("collectives.items.Course"),
        Workspace: this.$t("collectives.items.Workspace"),
        Challenge: this.$t("collectives.items.Challenge"),
        Organization: this.$t("collectives.items.Organization"),
        Service: this.$t("collectives.items.Service"),
      };
      return type[mainType][0].toUpperCase() + type[mainType].substring(1);
    },
    getColorSpace(transaction) {
      if(!transaction){
        return "info";
      }
      const color = {
        0: "light",
        1: "light-warning",
        2: "warning",
        3: "light-success",
        4: "light-danger",
        5: "light-dark",
        6: "secondary",
        7: "light-info",
        8: "info",
        81: "dark",
        Community: "primary",
        Subcommunity: "light-info",
        Event: "danger",
        Section: "success",
        Course: "info",
        Workspace: "dark",
        Challenge: "secondary",
        Organization: "light-info",
        Service: "light-warning",
      };
      return color[transaction];
    },
    getCollectiveUrl(collective) {
      return `${getCollectiveUrl(collective)}/welcome`;
    },
    resetForm() {
      this.currentTransaction = {
        ref: null,
        // modelType: "organization",
        // fromOrgKey: null,
        // modelKey: null,
        transactionEmitterType: "community",
        transactionEmitterKey: null,
        transactionCollectorType: "community",
        transactionCollectorKey: null,
        priceId: null,
        subscriptionPlanId: null,
        mainCommunitySlug: null,
        currencyId: 45,       //euro
        currencyPriceItemsWithoutFee: 0,
        currencyPriceItemsFee: 0,
        currencyAppFee: 0,
        currencyPriceTotal: 0,
        status: "succeeded",  //succeeded, pending, failed, refunded, canceled, requires_action, processing, requires_payment_method
        createdByUserId: null,
        //Billing
        hasBilling: 0,
        billingType: null,  //company/particular
        billingName: null,
        billingSurname: null,
        billingEmail: null,
        billingPhoneNumber: null,
        billingNifNieCif: null,
        billingCountryId: null,
        billingProvince: null,
        billingPostalCode: null,
        billingLocation: null,
        billingAddress1: null,
        billingAddress2: null,
      };
      this.transactionCollector = null;
      this.attachments = null;
      this.isEditing = false;
      this.editingTransactionKey = null;
      this.isValid = null;
    },
    closeModal() {
      this.$bvModal.hide("new-transaction");
    },
    editTransaction(transaction) {
      this.isEditing = true;
      
      // Populate the form with transaction data
      this.currentTransaction = {
        ...this.currentTransaction,
        ref: transaction.ref || null,
        transactionEmitterKey: transaction.emitter?.key || null,
        transactionCollectorKey: transaction.collector?.key || null,
        price: transaction.price || null,
        currencyId: transaction.currency?.id || 45,
        date: transaction.date || null,
        dueDate: transaction.dueDate || null
      };
      
      // Set collector if available
      if (transaction.collector) {
        this.transactionCollector = transaction.collector;
      }
      
      // Set attachments if available
      if (transaction.attachments) {
        this.attachments = transaction.attachments;
      }
      
      // Store the original transaction key for updating
      this.editingTransactionKey = transaction.key;
      
      // Open the modal
      this.$bvModal.show("new-transaction");
    },
    async deleteTransaction(transaction) {
      try {
        const itemData = {
          itemType: 'transactions',
          storedKey: "transactions",
          modelName: 'transaction',
          tableName: 'transactions',
          key: transaction.key,
          modelKey: transaction.key
        };

        await this.$store.dispatch('deleteItemGeneric', {
          item: itemData,
          customName: "transactions",
          noSet: true,
        });

        this.notifySuccess(
          this.$t("backoffice.transactions.messages.delete-success")
        );
        
        // Refresh the data
        await this.fetchData(true);
      } catch (error) {
        console.error('Error deleting transaction:', error);
        this.notifyError(this.$t("backoffice.transactions.messages.delete-error"));
      }
    },
    atFormShown() {
      return;
    },
    async saveTransaction() {
      try {
        //console.log("this.currentTransaction", this.currentTransaction);
        // const transaction = {
        //   name: this.currentTransaction.name,
        //   ref: this.currentTransaction.ref,
        //   price: this.currentTransaction.price,
        //   modelType: Array.isArray(this.currentTransaction.modelType)
        //     ? this.currentTransaction.modelType.map(
        //         (item) => item.addonSingular
        //       )
        //     : "",
        //   type: null,
        //   mainSpaceSlug: this.mainCollective.slug,
        // };

        // MODEL TYPE Y MODEL KEY en GentBorn y Intercontinental Alliance habla de quien está pagando, quien emite la transacción
        // COLLECTOR habla de quién está recibiendo el pago
        // this.currentTransaction.fromOrgKey = this.currentTransaction.fromOrgKey?.key;
        // this.currentTransaction.modelKey = this.currentTransaction.fromOrgKey?.key;
        //console.log("this.currentTransaction.modelKey", this.currentTransaction.modelKey);
        // this.currentTransaction.transactionCollectorKey = this.currentTransaction.modelKey; 
        this.currentTransaction.transactionCollectorKey = this.transactionCollector?.key;
        this.currentTransaction.mainSpaceSlug = this.mainSpace.slug;
        this.currentTransaction.mainCommunitySlug = this.mainSpace.slug;
        if(this.currentTransaction.transactionEmitterKey?.key){
          // It is an object, we need to save only the key
          this.currentTransaction.transactionEmitterKey = this.currentTransaction.transactionEmitterKey.key;
        }
        if (this.isEditing) {
          // Update existing transaction
          await this.$store.dispatch("editItem", {
            item: {
              itemType: "/transactions",
              customName: "transactions",
              requestConfig: {
                ...this.currentTransaction,
                key: this.editingTransactionKey, // Include the transaction key for updating
              },
            },
            file: this.attachments,
          });
          this.notifySuccess(
            this.$t("backoffice.transactions.messages.update-success")
          );
        } else {
          // Create new transaction
          await this.$store.dispatch("createItem", {
            item: {
              itemType: "/transactions",
              customName: "transactions",
              requestConfig: {
                ...this.currentTransaction,
              },
            },
            file: this.attachments,
          });
          this.notifySuccess(
            this.$t("backoffice.transactions.messages.create-success")
          );
        }
              } catch (error) {
          console.error('Error saving transaction:', error);
          if (this.isEditing) {
            this.notifyError(this.$t("backoffice.transactions.messages.update-error"));
          } else {
            this.notifyError(this.$t("backoffice.transactions.messages.create-error"));
          }
        }
        
        // Reset editing state
        this.isEditing = false;
      //TODO: Delete next when the previous works fine...
      window.location.reload();
      // await this.fetchData();
    },
    getLabel(labelObj) {
      return translate(labelObj.name);
    },
    getOptionsLabel() {
      // Only for organizations
      return this.organizationsOptions.map((organization) => this.translate(organization.name));
      //return this.organizationsOptions.filter((organization) => this.translate(organization.name));

      // General:
      // if(!this.modelOptions || this.modelOptions.length <= 0){
      //   return null;
      // }
      // return (a) => a.addon[this.currentLocale] || Object.values(a.addon)[0];
    },
    async fetchOrganizations() {
      const organizationName = this.currentCollective.mainType === "Organization" ? this.currentCollective.parentCollective.mainCommunityID ?  this.currentCollective.parentCollective.slug : this.mainSpace.slug : this.currentCollective.slug  
      const requestConfig = {
          communityParentSlug: organizationName,
          communitySlug: organizationName,
          transactionByDate: -1,
          checkStatus: true,
          isOrganization: true,
          avoidRedis: true,
          avoidShared: true,
          mainTypeIDs: 9,
          getMinimalData: true,
        };
        // console.log('requestConfig', requestConfig);
      return await this.$store.dispatch('getItems', {
        itemType: "communities/simply",
        customName: "communitiesOthers",
        storedKey: "organizations",
        page: 1,
        forceAPICall: true,
        perPage: 100,
        requestConfig: requestConfig,
      });
    },
    async fetchCurrencies() {
      //console.log('Fetching currency codes...');
      const response = await this.$store.$service[Service.BackendClient].get(
        'currencies',
        {
          params: {
            communityKey: this.currentCollective.key,
            count: 164,
            forceAPICall: true,
          },
        },
      );
      const currencyArray = response.data.data;
      for (const indexCurrency in currencyArray) {
        //console.log('indexCurrency', indexCurrency);
        const newCurrency = {
          value: currencyArray[indexCurrency].id,
          text: `${currencyArray[indexCurrency].ISO} (${currencyArray[indexCurrency].symbol})`,
          ...currencyArray[indexCurrency],
        };
        this.currencyCode.push(newCurrency);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.placeholder-img {
  width: 180px;
  display: block;
}

.transactions-modal {
  max-width: 90% !important;
  width: 90% !important;
}
</style>
